import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import PropTypes from 'prop-types';
import { Alert, Button, Card, Spinner } from '@openedx/paragon';
import { injectIntl, intlShape } from '@edx/frontend-platform/i18n';

import { getCourseSurveys } from './data/api';
import messages from './messages';
import SurveyForm from './SurveyForm';
import SurveyResults from './SurveyResults';

import './SurveyTab.scss';

const STATUS_MESSAGES = {
  not_answered: messages.notAnswered,
  answered: messages.completed,
};

const SurveyTab = ({ intl }) => {
  const { courseId } = useParams();
  const [surveys, setSurveys] = useState([]);
  const [selectedSurvey, setSelectedSurvey] = useState(null);
  const [viewedSurvey, setViewedSurvey] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [successMessageVisible, setSuccessMessageVisible] = useState(false);

  const loadSurveys = useCallback(async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const data = await getCourseSurveys(courseId);
      setSurveys((data || []).filter(survey => survey.is_active !== false));
    } catch (error) {
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, [courseId]);

  useEffect(() => {
    if (courseId) {
      loadSurveys();
    }
  }, [courseId, loadSurveys]);

  const handleReturn = () => {
    setSelectedSurvey(null);
    setViewedSurvey(null);
  };

  const handleSubmitted = async () => {
    setSelectedSurvey(null);
    setViewedSurvey(null);
    setSuccessMessageVisible(true);
    await loadSurveys();
  };

  const getStatusLabel = survey => (
    STATUS_MESSAGES[survey.status]
      ? intl.formatMessage(STATUS_MESSAGES[survey.status])
      : survey.status
  );

  const openSurvey = (survey) => {
    setSuccessMessageVisible(false);
    if (survey.status === 'answered') {
      setViewedSurvey(survey);
      setSelectedSurvey(null);
    } else {
      setSelectedSurvey(survey);
      setViewedSurvey(null);
    }
  };

  if (loading && !selectedSurvey && !viewedSurvey) {
    return (
      <div className="survey-loading">
        <Spinner
          animation="border"
          variant="primary"
          screenReaderText={intl.formatMessage(messages.loading)}
        />
      </div>
    );
  }

  if (loadError) {
    return <Alert variant="danger">{intl.formatMessage(messages.loadError)}</Alert>;
  }

  return (
    <div className="survey-tab-content my-4">
      {!selectedSurvey && !viewedSurvey && (
        <>
          <h1 className="h2 mb-3">{intl.formatMessage(messages.title)}</h1>

          {successMessageVisible && (
            <Alert variant="success">
              {intl.formatMessage(messages.answerSubmitted)}
            </Alert>
          )}

          {surveys.length === 0 && (
            <p className="text-gray-700">{intl.formatMessage(messages.noSurveys)}</p>
          )}

          {surveys.map(survey => (
            <Card key={survey.id} className="survey-card mb-3">
              <Card.Section>
                <div className="d-flex flex-column flex-md-row justify-content-between">
                  <div className="mr-md-3">
                    <span className={`badge mb-2 ${survey.status === 'answered' ? 'badge-success' : 'badge-light'}`}>
                      {getStatusLabel(survey)}
                    </span>
                    <h2 className="h4 mb-2">{survey.survey_name}</h2>
                    {survey.survey_description && (
                      <p className="text-gray-700 mb-0">{survey.survey_description}</p>
                    )}
                  </div>
                  <div className="survey-card-actions">
                    <div className="small text-gray-700 mb-2">
                      {intl.formatMessage(messages.questions, { count: survey.question_count || 0 })}
                    </div>
                    <Button
                      type="button"
                      variant="primary"
                      onClick={() => openSurvey(survey)}
                    >
                      {intl.formatMessage(survey.status === 'answered' ? messages.viewAnswers : messages.answerSurvey)}
                    </Button>
                  </div>
                </div>
              </Card.Section>
            </Card>
          ))}
        </>
      )}

      {selectedSurvey && (
        <SurveyForm
          courseSurvey={selectedSurvey}
          onCancel={handleReturn}
          onSubmitted={handleSubmitted}
        />
      )}

      {viewedSurvey && (
        <SurveyResults
          courseId={courseId}
          courseSurvey={viewedSurvey}
          onReturn={handleReturn}
        />
      )}
    </div>
  );
};

SurveyTab.propTypes = {
  intl: intlShape.isRequired,
};

export default injectIntl(SurveyTab);
